// Pure grid-building + axis-ordering for the heatmap primitive. Kept out of
// heatmap.jsx (which pulls in canvas/DOM code) so the ordering logic — the part
// that makes or breaks the chart — is unit-testable in a plain node env.
import { asNumber } from "../../lib/kernelFormat.js";

// Separator that can't appear in a real axis value, so composite keys never
// collide: with an empty/plain separator cellKey(1, 12) and cellKey(11, 2) both
// yield "112" (and any separator a value can contain collides too), silently
// merging two cells and distorting max.
const KEY_SEP = String.fromCharCode(0);

export function cellKey(x, y) {
    return String(x) + KEY_SEP + String(y);
}

// Indices that order `keys`: numeric / ISO-date keys sort by value (a time axis
// must read left-to-right in time); otherwise, when `totals` is given, by total
// desc (busiest first); otherwise insertion order (respect a kernel's
// intentional categorical order, e.g. Mon…Sun).
export function axisPermutation(keys, totals) {
    const idx = keys.map((_, i) => i);
    if (!keys.length) return idx;
    const isIso = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}/.test(s);
    const isNum = (s) => s != null && s !== "" && !Number.isNaN(Number(s));
    if (keys.every(isIso)) {
        return idx.sort((a, b) => (keys[a] < keys[b] ? -1 : keys[a] > keys[b] ? 1 : 0));
    }
    if (keys.every(isNum)) {
        return idx.sort((a, b) => Number(keys[a]) - Number(keys[b]));
    }
    if (totals) {
        return idx.sort((a, b) => (totals.get(keys[b]) ?? 0) - (totals.get(keys[a]) ?? 0));
    }
    return idx;
}

export function buildGrid(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    if (!arr.length) return null;
    const xField = spec.xField;
    const yField = spec.yField;
    const vField = spec.valueField;
    if (!xField || !yField || !vField) return null;
    const xOrder = [];
    const yOrder = [];
    const xSeen = new Set();
    const ySeen = new Set();
    const cells = new Map();
    const yTotals = new Map();
    let max = 0;
    for (const row of arr) {
        if (!row) continue;
        const x = row[xField];
        const y = row[yField];
        const v = asNumber(row[vField]) ?? 0;
        if (x == null || y == null) continue;
        if (!xSeen.has(x)) { xSeen.add(x); xOrder.push(x); }
        if (!ySeen.has(y)) { ySeen.add(y); yOrder.push(y); }
        const key = cellKey(x, y);
        const next = (cells.get(key) ?? 0) + v;
        cells.set(key, next);
        if (next > max) max = next;
        yTotals.set(y, (yTotals.get(y) ?? 0) + v);
    }
    // Order the axes. An unsorted TIME axis is the classic "useless heatmap": the
    // kernel emits rows grouped by category, so raw x insertion-order is
    // chronologically scrambled. Sort date/number axes by value; sort string
    // category rows by total desc so the busiest sit on top. Labels ride the same
    // permutation so they stay aligned to their keys.
    const xPerm = axisPermutation(xOrder, null);
    const yPerm = axisPermutation(yOrder, yTotals);
    const reorder = (a, perm) => (Array.isArray(a) ? perm.map((i) => a[i]) : null);
    return {
        xKeys: xPerm.map((i) => xOrder[i]),
        yKeys: yPerm.map((i) => yOrder[i]),
        xLabels: reorder(spec.xLabels, xPerm),
        yLabels: reorder(spec.yLabels, yPerm),
        cells,
        max,
    };
}
